import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { defaultDimensions } from '../../../utils/analytics';
import { getUserId } from '../../../utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '../../../utils/constants';
import Modal from '../../Modals';
import { NO_TRACE_LIMIT, TOP_SORT_DIRECTION } from '../constants';
import {
  Adjustments,
  Aggregates,
  Control,
  DiscountRate,
  EmissionsSource,
  Hydrocarbon,
  Metrics,
  MinMaxFilter,
  SortLimit,
} from './components';

// this will remove any unnecessary keys from state
const createState = state => {
  const newState = {};
  Object.keys(state).forEach(key => {
    if (state[key] !== undefined) {
      newState[key] = state[key];
    }
  });
  return newState;
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: 'calc(100% - 48px)',
    [theme.breakpoints.down('lg')]: {
      height: 'calc(100% - 36px)',
    },
  },
  modal: {
    backgroundColor: 'white',
    height: '85%',
    width: '90%',
    maxHeight: '600px',
    maxWidth: '1400px',
  },
  button: {
    ...theme.palette.button,
    width: '128px',
  },
  metricsContainer: {
    ...theme.palette.altScrollbarBlue,
    height: '100%',
    overflow: 'auto',
    padding: '4px 20px',
    // Only apply special layout to metric rows that have both radio options and checklists
    '& .MuiRadioGroup-root > div[data-has-options="true"]:has([data-component="graph-checklist"])':
      {
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: theme.spacing(2),
        '& > [data-component="graph-checklist"]': {
          flexBasis: '100%',
          width: '100%',
          marginTop: 0,
        },
      },
    '& .MuiRadioGroup-root': {
      '& > div': {
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
      },
    },
    '& .MuiTypography-root': {
      marginBottom: 0,
    },
    '& .MuiRadioGroup-root > div > div': {
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
  controls: {
    ...theme.palette.altScrollbarBlue,
    height: '100%',
    overflow: 'auto',
    padding: '20px',
    backgroundColor: '#f4f4f4f4',
  },
  controlsContainer: {
    height: 'calc(100% - 54.8px)',
    [theme.breakpoints.down('lg')]: {
      height: 'calc(100% - 48.8px)',
    },
  },
}));

const GraphControls = ({
  aggregate,
  aggregateTypes,
  aggregateLayers,
  checkboxAggregates,
  compareTo,
  compareToOptions = [],
  adjustments,
  setAdjustments,
  adjustmentsOptions,
  minMaxFilter,
  setMinMaxFilter,
  minMaxFilterOptions,
  loadingFilters,
  discountRate,
  emissionsSource,
  googleAnalyticsTag,
  graphType,
  graphTypeChildren,
  graphTypeOptions = [],
  hydrocarbonType,
  includeOptions,
  includeSortLimit,
  limit,
  open,
  setAggregate,
  setCheckboxAggregates,
  setCompareTo,
  setDiscountRate,
  setEmissionsSource,
  setGraphType,
  setGraphTypeOptions = () => {},
  setHydrocarbonType,
  setLimit,
  setOpen,
  setSizeType,
  setSortDirection,
  setSortMetric,
  setStackType,
  sizeType,
  sizeTypes,
  sortDirection,
  sortMetric,
  sortTypes,
  stackType,
  stackTypes,
  types,
  typesLabel = 'Select Metric',
}) => {
  const classes = useStyles();
  const [state, setState] = useState(
    createState({
      // any props that are undefined will not be included in state
      aggregate,
      checkboxAggregates,
      compareTo,
      discountRate,
      emissionsSource,
      graphType,
      graphTypeOptions,
      hydrocarbonType,
      limit,
      sizeType,
      sortDirection,
      sortMetric,
      stackType,
    }),
  );

  const handleSetAggregate = (
    aggregate,
    limit = NO_TRACE_LIMIT,
    sortDirection = TOP_SORT_DIRECTION,
  ) => {
    setState({ ...state, aggregate, limit, sortDirection });
  };

  const handleSetState = (value, name, layer) => {
    if (layer) {
      setState({ ...state, [name]: { ...state[name], [layer]: value } });
    } else {
      setState({ ...state, [name]: value });
    }
  };
  const handleMultiplesSetState = newState => {
    setState({ ...state, ...newState });
  };

  const handleApply = () => {
    const chartSettings = {
      aggregate: state.aggregate,
      graphType: state.graphType,
      graphTypeOptions: state.graphTypeOptions,
    };
    if (aggregate) {
      setAggregate(state.aggregate);
    }
    if (compareTo) {
      setCompareTo(state.compareTo);
    }

    if (adjustments && adjustmentsOptions?.length > 0) {
      const adjustmentValues = {};
      adjustmentsOptions.forEach(adjustment => {
        adjustmentValues[adjustment.value] = state[adjustment.value] || false;
      });
      setAdjustments(adjustmentValues);
    }

    if (minMaxFilter && minMaxFilterOptions) {
      const minMaxFilterValues = {
        [minMaxFilterOptions.min.key]: Number.isNaN(state[minMaxFilterOptions.min.key])
          ? null
          : parseFloat(state[minMaxFilterOptions.min.key]),
        [minMaxFilterOptions.max.key]: Number.isNaN(state[minMaxFilterOptions.max.key])
          ? null
          : parseFloat(state[minMaxFilterOptions.max.key]),
      };
      setMinMaxFilter({ ...minMaxFilter, ...minMaxFilterValues });
    }

    setGraphType(state.graphType);
    if (graphTypeOptions) {
      setGraphTypeOptions(state.graphTypeOptions);
    }
    if (checkboxAggregates) {
      setCheckboxAggregates(state.checkboxAggregates);
    }
    if (discountRate || discountRate === 0) {
      chartSettings.discountRate = state.discountRate;
      setDiscountRate(state.discountRate);
    }
    if (emissionsSource) {
      setEmissionsSource(state.emissionsSource);
    }
    if (hydrocarbonType) {
      chartSettings.hydrocarbonType = state.hydrocarbonType;
      setHydrocarbonType(state.hydrocarbonType);
    }
    if (limit || limit === '') {
      chartSettings.limit = state.limit;
      setLimit(state.limit);
    }
    if (sizeType) {
      setSizeType(state.sizeType);
    }
    if (sortDirection) {
      chartSettings.sortDirection = state.sortDirection;
      setSortDirection(state.sortDirection);
    }
    if (sortMetric) {
      chartSettings.sortMetric = state.sortMetric;
      setSortMetric(state.sortMetric);
    }
    if (stackType) {
      setStackType(state.stackType);
    }
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.chartSettings,
      event_label: JSON.stringify(chartSettings),
      userId: getUserId(),
      ...defaultDimensions,
    });

    setOpen(false);
  };

  // we need to sync discount rate with other component while keeping this component's state
  // biome-ignore lint/correctness/useExhaustiveDependencies: can't fix without breaking other things
  useEffect(() => {
    handleSetState(discountRate, 'discountRate');
  }, [discountRate]);

  return (
    <Modal
      handleClose={() => setOpen(false)}
      open={open}
      title='Chart Settings'
      titleAlignedWithClose={true}
      styles={classes}
      keepMounted
    >
      <Grid container className={classes.root} direction='column'>
        <Divider />
        <Grid className={classes.controlsContainer} container direction='row'>
          <Grid className={classes.metricsContainer} container size={6}>
            {types && (
              <Metrics
                label={typesLabel}
                graphTypes={types}
                graphTypeChildren={graphTypeChildren}
                handleSetState={handleSetState}
                includeOptions={includeOptions}
                itemIsDisabledParams={{
                  aggregate: state.aggregate,
                  stackType: state.stackType,
                  checkboxAggregates: state.checkboxAggregates,
                  sizeType: state.sizeType,
                  hydrocarbonType: state.hydrocarbonType,
                  graphTypeOptions: state.graphTypeOptions,
                }}
                selectedAggregate={state.aggregate}
                selectedGraphType={state.graphType}
                selectedGraphTypeOptions={state.graphTypeOptions}
              />
            )}
            {includeSortLimit && (
              <SortLimit
                aggregate={state.aggregate}
                handleSetAggregate={handleSetAggregate}
                handleSetState={handleSetState}
                itemIsDisabledProps={{ graphType: state.graphType }}
                limit={state.limit}
                sortDirection={state.sortDirection}
                sortMetric={state.sortMetric}
                sortTypes={sortTypes}
              />
            )}
          </Grid>
          <Grid
            className={types ? classes.controls : ''}
            style={!types ? { padding: '10px 20px' } : {}}
            container
            size={6}
          >
            <Control
              accessor='sizeType'
              handleSetState={handleSetState}
              itemIsDisabledParams={{ graphType: state.graphType }}
              label='Size'
              type={sizeType}
              types={sizeTypes}
              value={state.sizeType}
            />
            <EmissionsSource
              graphType={state.graphType}
              handleSetState={handleSetState}
              itemIsDisabledParams={{
                aggregate: state.aggregate,
              }}
              value={state.emissionsSource}
            />
            <DiscountRate
              discountRate={discountRate}
              graphType={state.graphType.label}
              handleSetState={handleSetState}
              value={state.discountRate}
            />
            <Hydrocarbon
              handleSetState={handleSetState}
              hydrocarbonType={hydrocarbonType}
              selectedCheckboxAggregates={state.checkboxAggregates}
              value={state.hydrocarbonType}
            />
            <Aggregates
              aggregateLayers={aggregateLayers}
              aggregateTypes={aggregateTypes}
              handleSetAggregate={handleSetAggregate}
              handleSetState={handleSetState}
              handleMultiplesSetState={handleMultiplesSetState}
              includeSortLimit={includeSortLimit}
              itemIsDisabledParams={{
                checkboxAggregates: state.checkboxAggregates,
                emissionsSource: state.emissionsSource,
                graphType: state.graphType,
                graphTypeOptions: state.graphTypeOptions,
                hydrocarbonType: state.hydrocarbonType,
                stackType: state.stackType,
              }}
              selectedAggregate={state.aggregate}
              selectedCheckboxAggregates={state.checkboxAggregates}
              selectedSortDirection={state.sortDirection}
              selectedGraphType={state.graphType}
              selectedGraphTypeOptions={state.graphTypeOptions}
            />
            <Control
              accessor='stackType'
              handleSetState={handleSetState}
              itemIsDisabledParams={{ aggregate: state.aggregate, graphType: state.graphType }}
              label='Stack Data'
              type={stackType}
              types={stackTypes}
              value={state.stackType}
            />
            <Control
              accessor='compareTo'
              handleSetState={handleSetState}
              itemIsDisabledParams={{ graphType: state.graphType }}
              label='Secondary Y Axis'
              type={compareTo}
              types={compareToOptions}
              value={state.compareTo}
            />
            <Adjustments
              values={adjustments}
              adjustments={adjustmentsOptions}
              handleSetState={handleSetState}
            />
            {!!minMaxFilterOptions && (
              <MinMaxFilter
                values={minMaxFilter}
                filter={minMaxFilterOptions}
                loading={loadingFilters}
                handleSetState={handleMultiplesSetState}
              />
            )}
          </Grid>
        </Grid>
        <Divider />
        <Grid
          container
          sx={{
            flex: '1 1 auto',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button variant='contained' className={classes.button} onClick={() => handleApply()}>
            Apply
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default GraphControls;
